// app/javascript/components/ColumnsConstructor/columnsUtils.js

// Типы колонок с описаниями
export const COLUMN_TYPE_DESCRIPTIONS = {
  base: 'Basic metric from stats data (page_loads, sms_success, etc.)',
  calculated: 'Calculated from other columns (sum, subtract, multiply)',
  percentage_ratio: 'Percentage ratio between two columns (A/B * 100)',
  percentage_attempts: 'Percentage from total attempts ((target/total) * 100)',
  error_category: 'Error category from ErrorCategory model'
};

// Стили для react-select
export const SELECT_STYLES = {
  control: (provided) => ({
    ...provided,
    borderColor: '#d1d5db',
    '&:hover': {
      borderColor: '#3b82f6'
    },
    '&:focus': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? '#3b82f6' 
      : state.isFocused 
        ? '#eff6ff' 
        : 'white',
    color: state.isSelected ? 'white' : '#374151'
  }),
  groupHeading: (provided) => ({
    ...provided,
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  })
};

// Генератор случайных цветов
export const generateRandomColor = () => {
  return "#" + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
};

// Функция для получения опций полей (группированные)
export const getFieldOptions = (availableFields = [], columns = [], forChartGroup = false) => {
  const baseFields = availableFields.map(([label, value]) => ({ 
    value, 
    label: `📊 ${label}`
  }));
  
  let existingColumns;
  if (forChartGroup) {
    existingColumns = columns
      .filter(col => col.column_type !== 'chart_group')
      .map(col => ({ 
        value: col.key, 
        label: `🔧 ${col.label} (${col.column_type})`
      }));
  } else {
    existingColumns = columns
      .filter(col => !['percentage_ratio', 'percentage_attempts', 'chart_group'].includes(col.column_type))
      .map(col => ({ 
        value: col.key, 
        label: `🔧 ${col.label} (${col.column_type})`
      }));
  }

  return [
    {
      label: '📊 Data Sources',
      options: baseFields
    },
    {
      label: '🔧 Created Columns', 
      options: existingColumns
    }
  ];
};

// Функция для получения всех опций в плоском виде (для поиска)
export const getAllFieldOptions = (availableFields = [], columns = [], forChartGroup = false) => {
  const fieldOptions = getFieldOptions(availableFields, columns, forChartGroup);
  const allOptions = [];
  fieldOptions.forEach(group => {
    if (group.options) {
      allOptions.push(...group.options);
    }
  });
  return allOptions;
};

// Функция для поиска выбранного значения
export const findSelectedValue = (value, availableFields = [], columns = [], forChartGroup = false) => {
  if (!value) return null;
  const allOptions = getAllFieldOptions(availableFields, columns, forChartGroup);
  return allOptions.find(opt => opt.value === value) || null;
};

// Функция для поиска выбранных значений (мультиселект)
export const findSelectedValues = (values, availableFields = [], columns = [], forChartGroup = false) => {
  if (!values || !Array.isArray(values)) return [];
  const allOptions = getAllFieldOptions(availableFields, columns, forChartGroup);
  return allOptions.filter(opt => values.includes(opt.value));
};

// Опции для error categories
export const getErrorCategoryOptions = (errorCategories = []) => {
  return errorCategories.map(([name, id]) => ({
    value: `error_category_id:${id}`,
    label: `⚠️ ${name}`
  }));
};

// Функция получения иконки для типа колонки
export const getColumnTypeIcon = (columnType) => {
  const iconMap = {
    base: 'BarChart3',
    calculated: 'Settings',
    percentage_ratio: 'BarChart3',
    percentage_attempts: 'BarChart3',
    error_category: 'AlertTriangle',
    chart_group: 'Layers'
  };
  return iconMap[columnType] || 'BarChart3';
};

// Функция получения CSS классов для типа колонки
export const getColumnTypeBadgeClass = (columnType) => {
  const classMap = {
    base: 'bg-blue-100 text-blue-800',
    calculated: 'bg-purple-100 text-purple-800',
    percentage_ratio: 'bg-green-100 text-green-800',
    percentage_attempts: 'bg-orange-100 text-orange-800',
    error_category: 'bg-red-100 text-red-800',
    chart_group: 'bg-indigo-100 text-indigo-800'
  };
  return classMap[columnType] || 'bg-gray-100 text-gray-800';
};

// Валидация формы колонки
export const validateColumnForm = (formData) => {
  const errors = {};
  
  if (!formData.key?.trim()) {
    errors.key = 'Column key is required';
  }
  
  if (!formData.label?.trim()) {
    errors.label = 'Display label is required';
  }
  
  if (!formData.column_type) {
    errors.column_type = 'Column type is required';
  }
  
  // Валидация на основе типа колонки
  switch (formData.column_type) {
    case 'base':
      if (!formData.data_source) {
        errors.data_source = 'Data source is required for base columns';
      }
      break;
      
    case 'calculated':
      const config = formData.calculation_config || {};
      if (!config.formula) {
        errors.formula = 'Formula is required for calculated columns';
      }
      if (!config.source_columns || config.source_columns.length === 0) {
        errors.source_columns = 'Source columns are required for calculated columns';
      }
      break;
      
    case 'percentage_ratio':
      const ratioConfig = formData.calculation_config || {};
      if (!ratioConfig.numerator) {
        errors.numerator = 'Numerator is required for percentage ratio';
      }
      if (!ratioConfig.denominator) {
        errors.denominator = 'Denominator is required for percentage ratio';
      }
      break;
      
    case 'percentage_attempts':
      const attemptsConfig = formData.calculation_config || {};
      if (!attemptsConfig.target) {
        errors.target = 'Target field is required for percentage attempts';
      }
      if (!attemptsConfig.total_columns || attemptsConfig.total_columns.length === 0) {
        errors.total_columns = 'Total columns are required for percentage attempts';
      }
      break;
      
    case 'error_category':
      if (!formData.data_source) {
        errors.data_source = 'Error category is required';
      }
      break;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Валидация формы chart group
export const validateChartGroupForm = (formData) => {
  const errors = {};
  
  if (!formData.key?.trim()) {
    errors.key = 'Group key is required';
  }
  
  if (!formData.label?.trim()) {
    errors.label = 'Display label is required';
  }
  
  if (!formData.group_name?.trim()) {
    errors.group_name = 'Group name is required';
  }
  
  if (!formData.metrics || formData.metrics.length < 2) {
    errors.metrics = 'At least 2 metrics are required for chart group';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Инициализация формы для колонок
export const initColumnForm = (column = null) => {
  if (column) {
    return {
      id: column.id,
      key: column.key || '',
      label: column.label || '',
      description: column.description || '',
      column_type: column.column_type || 'base',
      data_source: column.data_source || '',
      chart_color: column.chart_color || '#10B981',
      is_visible: column.is_visible !== false,
      show_on_chart: column.show_on_chart || false,
      calculation_config: column.calculation_config || {}
    };
  } else {
    return {
      id: null,
      key: '',
      label: '',
      description: '',
      column_type: 'base',
      data_source: '',
      chart_color: '#10B981',
      is_visible: true,
      show_on_chart: false,
      calculation_config: {}
    };
  }
};

// Инициализация формы для chart groups
export const initChartGroupForm = (chartGroup = null) => {
  if (chartGroup) {
    return {
      id: chartGroup.id,
      key: chartGroup.key || '',
      label: chartGroup.label || '',
      group_name: chartGroup.group_name || '',
      metrics: chartGroup.metrics || [],
      colors: chartGroup.colors || {},
      chart_color: chartGroup.chart_color || '#10B981'
    };
  } else {
    return {
      id: null,
      key: '',
      label: '',
      group_name: '',
      metrics: [],
      colors: {},
      chart_color: '#10B981'
    };
  }
};

// Обработка изменения цвета метрики в chart_group
export const handleMetricColorChange = (formData, setFormData, metric, color) => {
  setFormData(prev => ({
    ...prev,
    colors: {
      ...prev.colors,
      [metric]: color
    }
  }));
};

// Специальная обработка для chart_group метрик
export const handleChartGroupMetricsChange = (formData, setFormData, selectedOptions) => {
  const metrics = selectedOptions ? selectedOptions.map(option => option.value) : [];
  
  const currentColors = formData.colors || {};
  const newColors = { ...currentColors };
  
  metrics.forEach(metric => {
    if (!newColors[metric]) {
      newColors[metric] = generateRandomColor();
    }
  });
  
  Object.keys(newColors).forEach(metric => {
    if (!metrics.includes(metric)) {
      delete newColors[metric];
    }
  });
  
  setFormData(prev => ({
    ...prev,
    metrics: metrics,
    colors: newColors
  }));
};